import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Button, Stack, IconButton } from "@mui/material";
import { arrayOf, element, func, node, oneOfType, shape, string } from "prop-types";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Edit, Visibility, Menu } from "@mui/icons-material";
import AddToLibrary from "../components/shared/AddToLibrary";
import Authenticated from "./Authenticated";
import { AuthenticatedMenuContext } from "./authenticatedMenuContext";
import { Fragment, useContext } from "react";
import TagsRelationshipContainer from "../components/pages/Cuisine/TagsContainer";
import { Link } from "react-router-dom";
import RecipeSummary from "../components/RecipeSummary";
import AlertBox from "../components/AlertBox";
import DeleteDialog from "../components/DeleteDialog";

const CreatePageContent = ({
  children,
  title,
  summaries = [],
  instructionMessage,
  breadcrumbsLinks = [],
  actionOptions = [],
  onDelete,
  addToLibrary = {},
  relationships = [],
  editUrl,
  previewUrl,
  onMenuToggle: onMenuToggleProp,
}) => {
  const onMenuToggleFromLayout = useContext(AuthenticatedMenuContext);
  const onMenuToggle = onMenuToggleProp ?? onMenuToggleFromLayout;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Stack
        spacing={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
        >
          <IconButton
            aria-label="open drawer"
            onClick={onMenuToggle}
            sx={{
              display: {
                xs: 'block',
                md: 'none',
              },
              color: '#000',
              p: 1,
            }}
          >
            <Menu fontSize="medium" />
          </IconButton>

          <Box>
            <Typography className="form-title" variant="h2">
              {title}
            </Typography>

            <Breadcrumbs home links={breadcrumbsLinks} />
          </Box>
        </Stack>

        <Stack
          direction={"row"}
          spacing={0.5}
          alignItems={"center"}
        >
          <AddToLibrary
            junctionTable={addToLibrary.junctionTable}
            filter={addToLibrary.filter}
            addPayload={addToLibrary.addPayload}
            removePayload={addToLibrary.removePayload}
          />

          {
            !!editUrl &&
            <Link
              to={editUrl}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="outlined"
              >
                <Edit />
              </Button>
            </Link>
          }

          {
            !!previewUrl &&
            <a
              href={previewUrl}
              target="_blank"
            >
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  ml: 1,
                }}
              >
                <Visibility />
              </Button>
            </a>
          }

          {!!onDelete && <DeleteDialog />}
        </Stack>
      </Stack>

      {relationships?.length > 0 && (
        <Stack
          direction={"row"}
          spacing={1}
          sx={{
            my: 2,
          }}
        >
          {relationships.map((relationship) => (
            <Fragment key={relationship.name}>
              <TagsRelationshipContainer
                name={relationship.name}
                description={relationship.description}
                count={relationship.count}
                columns={relationship.columns}
                junctionTable={relationship.junctionTable}
                mainTable={relationship.mainTable}
                mainTablePayload={relationship.mainTablePayload}
                mainTableResponseHandler={
                  relationship.mainTableResponseHandler
                }
                payload={relationship.payload}
                requestHandler={relationship.requestHandler}
                responseHandler={relationship.responseHandler}
              />
            </Fragment>
          ))}
        </Stack>
      )}

      {instructionMessage && <AlertBox message={instructionMessage} />}

      <RecipeSummary summaryValues={summaries} />

      {children}
    </Box>
  );
};

const CreatePage = (props) => {
  return (
    <Authenticated>
      <CreatePageContent {...props} />
    </Authenticated>
  );
};

CreatePage.propTypes = {
  children: node.isRequired,
  title: string.isRequired,
  instructionMessage: string,
  breadcrumbsLinks: arrayOf(
    shape({
      url: string,
      content: oneOfType([string, element]).isRequired,
    })
  ),
  actionOptions: arrayOf(
    shape({
      key: string.isRequired,
      content: oneOfType([string, element]).isRequired,
      onClick: func.isRequired,
    })
  ),
  onDelete: func,
  onMenuToggle: func,
};

export default CreatePage;
